import {
  Badge,
  Button,
  Checkbox,
  Code,
  CopyButton,
  Group,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconCopy, IconDownload, IconTrash } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  APP_PASSWORD_DEFAULT_DAYS,
  APP_PASSWORD_SCOPES,
  type AppPasswordScope,
} from "../../shared/constants";
import type { MessageKey } from "../../shared/i18n";
import { LOCALES, type Locale } from "../../shared/i18n";
import { type AppPassword, endpoints } from "../api/client";
import { appPasswordsQuery, meQuery, useNotifyError } from "../api/queries";
import { authClient } from "../auth/client";
import { useAuth } from "../auth/provider";
import { AppFrame } from "../components/app-frame";
import { ConfirmDialog } from "../components/confirm-dialog";
import { Page, PageHeader } from "../components/page";
import { formatDate, formatRelative } from "../i18n/format";
import { useLocale, useMessages } from "../i18n/provider";
import styles from "./settings.module.css";

const SCOPE_KEYS: Record<AppPasswordScope, MessageKey> = {
  "resumes:read": "settings.scopeRead",
  "resumes:write": "settings.scopeWrite",
  "resumes:publish": "settings.scopePublish",
  "assets:write": "settings.scopeAssets",
};

type Tab = "profile" | "security" | "app-passwords" | "language" | "danger";

export function SettingsPage() {
  const t = useMessages();
  const { locale, setLocale } = useLocale();
  const { me, refresh } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const [params, setParams] = useSearchParams();

  const initialTab = params.get("tab");
  const [tab, setTab] = useState<Tab>(initialTab === "app-passwords" ? "app-passwords" : "profile");

  const changeTab = (value: string | null) => {
    const next = (value ?? "profile") as Tab;
    setTab(next);
    setParams(next === "app-passwords" ? { tab: "app-passwords" } : {}, {
      replace: true,
    });
  };

  const [name, setName] = useState(me?.name ?? "");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    setName(me?.name ?? "");
  }, [me?.name]);

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === me?.name) return;
    setSavingName(true);
    try {
      await endpoints.updateMe({ name: trimmed });
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
      await refresh();
      notifications.show({
        color: "gray",
        message: t("settings.saved"),
        autoClose: 2000,
      });
    } catch (error) {
      notifyError(error);
    } finally {
      setSavingName(false);
    }
  };

  const changeLanguage = async (next: Locale) => {
    setLocale(next);
    try {
      await endpoints.updateMe({ locale: next });
      await queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <AppFrame active="/settings">
      <Page width="narrow">
        <PageHeader title={t("settings.title")} />

        <Tabs value={tab} onChange={changeTab} variant="pills" radius="md">
          <Tabs.List className={styles.tabs}>
            <Tabs.Tab value="profile">{t("settings.profileTab")}</Tabs.Tab>
            <Tabs.Tab value="security">{t("settings.securityTab")}</Tabs.Tab>
            <Tabs.Tab value="app-passwords">{t("settings.appPasswordsTab")}</Tabs.Tab>
            <Tabs.Tab value="language">{t("settings.languageTab")}</Tabs.Tab>
            <Tabs.Tab value="danger">{t("settings.dangerTab")}</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="profile" pt="lg">
            <Stack gap="md" className={styles.form}>
              <TextInput
                label={t("settings.displayName")}
                value={name}
                onChange={(event) => setName(event.currentTarget.value)}
                maxLength={120}
              />
              <TextInput
                label={t("settings.emailReadonly")}
                value={me?.email ?? ""}
                readOnly
                variant="filled"
              />
              <Group>
                <Button
                  onClick={() => void saveName()}
                  loading={savingName}
                  disabled={!name.trim() || name.trim() === me?.name}
                >
                  {t("common.save")}
                </Button>
              </Group>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="security" pt="lg">
            <SecurityPanel />
          </Tabs.Panel>

          <Tabs.Panel value="app-passwords" pt="lg">
            <AppPasswordsPanel />
          </Tabs.Panel>

          <Tabs.Panel value="language" pt="lg">
            <Stack gap="md" className={styles.form}>
              <Text size="sm" c="dimmed">
                {t("settings.languageHint")}
              </Text>
              <Group gap="xs">
                {LOCALES.map((value) => (
                  <Button
                    key={value}
                    variant={value === locale ? "filled" : "default"}
                    size="sm"
                    onClick={() => void changeLanguage(value)}
                  >
                    {value === "en" ? t("common.english") : t("common.vietnamese")}
                  </Button>
                ))}
              </Group>
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="danger" pt="lg">
            <DangerPanel
              onDeleted={async () => {
                await authClient.signOut();
                queryClient.clear();
                navigate("/");
              }}
            />
          </Tabs.Panel>
        </Tabs>
      </Page>
    </AppFrame>
  );
}

function SecurityPanel() {
  const t = useMessages();
  const notifyError = useNotifyError();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const result = await authClient.changePassword({
        currentPassword: current,
        newPassword: next,
        revokeOtherSessions: true,
      });
      if (result.error) {
        notifications.show({
          color: "red",
          message: t("settings.wrongPassword"),
        });
        return;
      }
      notifications.show({
        color: "gray",
        message: t("settings.passwordChanged"),
      });
      setCurrent("");
      setNext("");
    } catch (error) {
      notifyError(error);
    } finally {
      setBusy(false);
    }
  };

  const revokeOthers = async () => {
    setRevoking(true);
    try {
      const result = await authClient.revokeOtherSessions();
      if (result.error) {
        notifications.show({ color: "red", message: t("errors.generic") });
        return;
      }
      notifications.show({
        color: "gray",
        message: t("settings.sessionsRevoked"),
      });
    } catch (error) {
      notifyError(error);
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Stack gap="lg" className={styles.form}>
      <Stack gap="md">
        <PasswordInput
          label={t("settings.currentPassword")}
          value={current}
          onChange={(event) => setCurrent(event.currentTarget.value)}
          autoComplete="current-password"
        />
        <PasswordInput
          label={t("settings.newPassword")}
          value={next}
          onChange={(event) => setNext(event.currentTarget.value)}
          description={t("auth.passwordHint")}
          autoComplete="new-password"
        />
        <Group>
          <Button
            onClick={() => void submit()}
            loading={busy}
            disabled={!current || next.length < 8}
          >
            {t("settings.changePassword")}
          </Button>
        </Group>
      </Stack>
      <Button
        variant="default"
        onClick={() => void revokeOthers()}
        loading={revoking}
        className={styles.selfStart}
      >
        {t("settings.signOutEverywhere")}
      </Button>
    </Stack>
  );
}

function AppPasswordsPanel() {
  const t = useMessages();
  const { locale } = useLocale();
  const queryClient = useQueryClient();
  const notifyError = useNotifyError();
  const { data } = useQuery(appPasswordsQuery());
  const items = data?.items ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState<string[]>([...APP_PASSWORD_SCOPES]);
  const [expiry, setExpiry] = useState(String(APP_PASSWORD_DEFAULT_DAYS));
  const [creating, setCreating] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<AppPassword | null>(null);

  const create = async () => {
    setCreating(true);
    try {
      const result = await endpoints.createAppPassword({
        name: name.trim(),
        scopes: scopes as AppPasswordScope[],
        expiresInDays: Number(expiry),
      });
      await queryClient.invalidateQueries({
        queryKey: appPasswordsQuery().queryKey,
      });
      setCreateOpen(false);
      setName("");
      setScopes([...APP_PASSWORD_SCOPES]);
      setExpiry(String(APP_PASSWORD_DEFAULT_DAYS));
      setToken(result.token);
    } catch (error) {
      notifyError(error);
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (password: AppPassword) => {
    try {
      await endpoints.deleteAppPassword(password.id);
      await queryClient.invalidateQueries({
        queryKey: appPasswordsQuery().queryKey,
      });
    } catch (error) {
      notifyError(error);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start">
        <Text size="sm" c="dimmed" maw={420}>
          {t("settings.appPasswordLimit")}
        </Text>
        <Button size="sm" onClick={() => setCreateOpen(true)} disabled={items.length >= 10}>
          {t("settings.newAppPassword")}
        </Button>
      </Group>

      {items.length === 0 ? (
        <Text size="sm" c="dimmed">
          {t("settings.noAppPasswords")}
        </Text>
      ) : (
        <Stack gap="sm">
          {items.map((password) => (
            <div key={password.id} className={styles.passwordRow}>
              <div className={styles.passwordText}>
                <Group gap={8}>
                  <Text fw={600} size="sm">
                    {password.name}
                  </Text>
                  <Code>{password.prefix}…</Code>
                </Group>
                <Group gap={6} mt={6}>
                  {password.scopes.map((scope) => (
                    <Badge key={scope} size="xs" variant="light" color="gray">
                      {t(SCOPE_KEYS[scope])}
                    </Badge>
                  ))}
                </Group>
                <Text size="xs" c="dimmed" mt={6}>
                  {password.lastUsedAt
                    ? t("settings.lastUsed", {
                        when: formatRelative(password.lastUsedAt, locale),
                      })
                    : t("settings.never")}
                  {password.expiresAt
                    ? ` · ${t("settings.expiresOn", {
                        when: formatDate(password.expiresAt, locale),
                      })}`
                    : ""}
                </Text>
              </div>
              <Button
                variant="subtle"
                color="red"
                size="compact-sm"
                leftSection={<IconTrash size={14} stroke={1.5} />}
                onClick={() => setRevoking(password)}
              >
                {t("settings.revoke")}
              </Button>
            </div>
          ))}
        </Stack>
      )}

      <Modal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
        title={t("settings.newAppPassword")}
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label={t("settings.appPasswordName")}
            value={name}
            onChange={(event) => setName(event.currentTarget.value)}
            maxLength={60}
            data-autofocus
          />
          <Checkbox.Group label={t("settings.scopes")} value={scopes} onChange={setScopes}>
            <Stack gap={6} mt={6}>
              {APP_PASSWORD_SCOPES.map((scope) => (
                <Checkbox key={scope} value={scope} label={t(SCOPE_KEYS[scope])} />
              ))}
            </Stack>
          </Checkbox.Group>
          <Select
            label={t("settings.expiry")}
            value={expiry}
            onChange={(value) => setExpiry(value ?? "90")}
            allowDeselect={false}
            data={[
              { value: "30", label: t("settings.expiry30") },
              { value: "90", label: t("settings.expiry90") },
              { value: "365", label: t("settings.expiry365") },
            ]}
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => void create()}
              loading={creating}
              disabled={!name.trim() || scopes.length === 0}
            >
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={Boolean(token)}
        onClose={() => setToken(null)}
        title={t("settings.tokenTitle")}
        size="md"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t("settings.tokenWarning")}
          </Text>
          <Code block className={styles.token}>
            {token}
          </Code>
          <Group justify="flex-end">
            {token ? (
              <CopyButton value={token}>
                {({ copied, copy }) => (
                  <Button
                    variant="light"
                    leftSection={<IconCopy size={16} stroke={1.5} />}
                    onClick={copy}
                  >
                    {copied ? t("common.copied") : t("common.copy")}
                  </Button>
                )}
              </CopyButton>
            ) : null}
            <Button onClick={() => setToken(null)}>{t("common.done")}</Button>
          </Group>
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        onConfirm={async () => {
          if (revoking) await revoke(revoking);
        }}
        title={t("settings.revoke")}
        body={t("settings.revokeConfirm")}
        confirmLabel={t("settings.revoke")}
      />
    </Stack>
  );
}

function DangerPanel({ onDeleted }: { onDeleted: () => Promise<void> }) {
  const t = useMessages();
  const notifyError = useNotifyError();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const exportData = async () => {
    try {
      const data = await endpoints.exportMe();
      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `yourcv-export-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      notifyError(error);
    }
  };

  const deleteAccount = async () => {
    setBusy(true);
    try {
      await endpoints.deleteMe();
      await onDeleted();
    } catch (error) {
      notifyError(error);
      setBusy(false);
    }
  };

  return (
    <Stack gap="lg" className={styles.form}>
      <div>
        <Button
          variant="default"
          leftSection={<IconDownload size={16} stroke={1.5} />}
          onClick={() => void exportData()}
        >
          {t("settings.exportData")}
        </Button>
      </div>

      <div className={styles.danger}>
        <Text fw={600} size="sm">
          {t("settings.deleteAccount")}
        </Text>
        <Text size="sm" c="dimmed" mt={4}>
          {t("settings.dangerBody")}
        </Text>
        <TextInput
          mt="md"
          label={t("settings.deleteConfirm")}
          value={confirm}
          onChange={(event) => setConfirm(event.currentTarget.value)}
          placeholder="DELETE"
        />
        <Button
          mt="md"
          color="red"
          disabled={confirm !== "DELETE"}
          onClick={() => setConfirmOpen(true)}
          leftSection={<IconCheck size={16} stroke={1.5} />}
        >
          {t("settings.deleteAccount")}
        </Button>
      </div>

      <ConfirmDialog
        opened={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={deleteAccount}
        title={t("settings.deleteAccount")}
        body={t("settings.dangerBody")}
        confirmLabel={t("common.delete")}
        loading={busy}
      />
    </Stack>
  );
}
